import React, { useState } from 'react';
import { X, Edit, Bot, Settings, ChevronDown, ChevronRight, Eye, Calendar, User, Code } from 'lucide-react';
import { Agent, Model, OutputType } from '../../types';
import { useProjectStore } from '../../store';
import ModelSchemaPreview from './ModelSchemaPreview';
import Tooltip from '../common/Tooltip';

interface AgentDetailsModalProps {
  agent: Agent;
  models: Model[];
  onClose: () => void;
  onEdit: () => void;
}

const AgentDetailsModal: React.FC<AgentDetailsModalProps> = ({ agent, models, onClose, onEdit }) => {
  const { currentProject } = useProjectStore();
  const [expandedSections, setExpandedSections] = useState({
    basic: true,
    hints: true,
    output: true,
    settings: false,
    tools: false,
    advanced: false
  });
  const [showModelPreview, setShowModelPreview] = useState(false);

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const getOutputTypeColor = (type: OutputType) => {
    const colors = {
      str: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200',
      model: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-300'
    };
    return colors[type] || 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
  };

  const attachedModel = agent.output_model_id 
    ? models.find(m => m.model_id === agent.output_model_id) || agent.output_model
    : null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-cap-dark rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <Bot className="w-8 h-8 text-primary-600 mr-3" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-warm-white">{agent.name}</h2>
              <div className="flex items-center space-x-2 mt-1">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getOutputTypeColor(agent.output_type || 'str')}`}>
                  {agent.output_type || 'str'}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={onEdit}
              className="px-4 py-2 text-sm font-medium text-blue-700 dark:text-blue-300 bg-primary-50 dark:bg-primary-900/30 border border-primary-200 dark:border-primary-800 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-800/40 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:text-gray-400 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-120px)]">
          <div className="p-6 space-y-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <button
                type="button"
                onClick={() => toggleSection('basic')}
                className="flex items-center w-full text-left"
              >
                {expandedSections.basic ? (
                  <ChevronDown className="w-5 h-5 mr-2" />
                ) : (
                  <ChevronRight className="w-5 h-5 mr-2" />
                )}
                <h3 className="text-lg font-medium text-gray-900 dark:text-warm-white">Basic Information</h3>
              </button>

              {expandedSections.basic && (
                <div className="pl-7 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Project
                    </label>
                    <div className="text-sm text-gray-900 dark:text-warm-white font-mono bg-gray-50 dark:bg-gray-900 px-3 py-2 rounded-lg">
                      {currentProject?.name || 'Unknown Project'}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Instructions
                    </label>
                    <div className="text-sm text-gray-900 dark:text-warm-white bg-gray-50 dark:bg-gray-900 px-3 py-2 rounded-lg whitespace-pre-wrap">
                      {agent.instructions || 'No instructions provided'}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Created
                      </label>
                      <div className="flex items-center text-sm text-gray-900 dark:text-warm-white">
                        <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                        {new Date(agent.created_at).toLocaleString()}
                      </div>
                    </div>
                    {agent.updated_at && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Last Updated
                        </label>
                        <div className="flex items-center text-sm text-gray-900 dark:text-warm-white">
                          <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                          {new Date(agent.updated_at).toLocaleString()}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Hints */}
            {agent.hints && (
              <div className="space-y-4">
                <button
                  type="button"
                  onClick={() => toggleSection('hints')}
                  className="flex items-center w-full text-left"
                >
                  {expandedSections.hints ? (
                    <ChevronDown className="w-5 h-5 mr-2" />
                  ) : (
                    <ChevronRight className="w-5 h-5 mr-2" />
                  )}
                  <h3 className="text-lg font-medium text-gray-900 dark:text-warm-white">Tool Hints</h3>
                </button>

                {expandedSections.hints && (
                  <div className="pl-7 space-y-4">
                    <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-3 mb-4">
                      <p className="text-sm text-blue-800 dark:text-blue-300">
                        <strong>Note:</strong> These hints are automatically generated based on the agent's tools and are merged with instructions during execution. They are read-only and cannot be modified directly.
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Auto-Generated Hints
                      </label>
                      <div className="text-sm text-gray-900 dark:text-warm-white bg-gray-50 dark:bg-gray-900 px-3 py-2 rounded-lg whitespace-pre-wrap border-l-4 border-blue-400">
                        {agent.hints}
                      </div>
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        These hints are automatically updated when tools are modified and help the agent understand how to use its available tools
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Tools */}
            {agent.tools && agent.tools.length > 0 && (
              <div className="space-y-4">
                <button
                  type="button"
                  onClick={() => toggleSection('tools')}
                  className="flex items-center w-full text-left"
                >
                  {expandedSections.tools ? (
                    <ChevronDown className="w-5 h-5 mr-2" />
                  ) : (
                    <ChevronRight className="w-5 h-5 mr-2" />
                  )}
                  <h3 className="text-lg font-medium text-gray-900 dark:text-warm-white">Tools ({agent.tools.length})</h3>
                </button>

                {expandedSections.tools && (
                  <div className="pl-7 space-y-3">
                    {agent.tools.map((tool, index) => (
                      <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="font-medium text-gray-900 dark:text-warm-white">{tool.name}</div>
                          <span className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 px-2 py-1 rounded">
                            {tool.type}
                          </span>
                        </div>
                        {tool.description && (
                          <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">{tool.description}</div>
                        )}
                        {tool.user_location && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            Location: {tool.user_location.city}, {tool.user_location.region}, {tool.user_location.country}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Response Formatting */}
            <div className="space-y-4">
              <button
                type="button"
                onClick={() => toggleSection('output')}
                className="flex items-center w-full text-left"
              >
                {expandedSections.output ? (
                  <ChevronDown className="w-5 h-5 mr-2" />
                ) : (
                  <ChevronRight className="w-5 h-5 mr-2" />
                )}
                <h3 className="text-lg font-medium text-gray-900 dark:text-warm-white">Response Formatting</h3>
              </button>

              {expandedSections.output && (
                <div className="pl-7 space-y-4">
                  <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-3 mb-4">
                    <p className="text-sm text-blue-800 dark:text-blue-300">
                      <strong>Note:</strong> These settings control how the agent's responses are structured and formatted,
                      not the underlying language model behavior.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Response Format
                    </label>
                    <div className="text-sm text-gray-900 dark:text-warm-white bg-gray-50 dark:bg-gray-900 px-3 py-2 rounded-lg">
                      {agent.output_type === 'model' ? 'Structured Data (Model)' : 'Plain Text'}
                    </div>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      How the agent's responses are formatted and structured
                    </p>
                  </div>

                  {attachedModel && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Data Structure Model
                      </label>
                      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium text-gray-900 dark:text-warm-white">{attachedModel.class_name}</div>
                            {attachedModel.description && (
                              <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">{attachedModel.description}</div>
                            )}
                          </div>
                          <Tooltip content="View schema">
                            <button
                              onClick={() => setShowModelPreview(true)}
                              className="p-2 text-gray-400 hover:text-primary-600 transition-colors"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                          </Tooltip>
                        </div>
                      </div>
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        The data model that defines the structure and validation rules for responses
                      </p>
                      {showModelPreview && attachedModel && (
                        <ModelSchemaPreview
                          model={attachedModel}
                          allModels={models}
                          onClose={() => setShowModelPreview(false)}
                          label="Model Preview"
                        />
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Advanced Settings */}
            <div className="space-y-4">
              <button
                type="button"
                onClick={() => toggleSection('advanced')}
                className="flex items-center w-full text-left"
              >
                {expandedSections.advanced ? (
                  <ChevronDown className="w-5 h-5 mr-2" />
                ) : (
                  <ChevronRight className="w-5 h-5 mr-2" />
                )}
                <h3 className="text-lg font-medium text-gray-900 dark:text-warm-white">Advanced Settings</h3>
              </button>

              {expandedSections.advanced && (
                <div className="pl-7 space-y-4">
                  {/* Model Configuration */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Language Model
                    </label>
                    <div className="text-sm text-gray-900 dark:text-warm-white bg-gray-50 dark:bg-gray-900 px-3 py-2 rounded-lg">
                      {agent.model || 'Not specified'}
                    </div>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      The underlying language model that processes instructions and generates responses
                    </p>
                  </div>

                  {/* Model Settings */}
                  {agent.model_settings && Object.keys(agent.model_settings).length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Model Settings
                      </label>
                      <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                        <pre className="text-sm text-gray-900 dark:text-warm-white overflow-x-auto">
                          {JSON.stringify(agent.model_settings, null, 2)}
                        </pre>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentDetailsModal;